import { ApiError } from "@jobenvoy/core";
import type { Posting } from "@/lib/api/catalogue";
import { describeError } from "@/lib/api/client";
import type { Application, ApplicationFit } from "@/lib/api/envoy";
import type { LlmModel } from "@/lib/api/gateway";
import type { PostingFit } from "@/lib/api/resumeService";
import { formatRelative } from "@/lib/format";
import { dayOf } from "./dates";
import { payLine } from "./facts";
import { fitAtMatch, fitNow, type FitView } from "./fit";

/**
 * The posting page's own decisions: what it says when the posting cannot
 * be read, which fit it shows, and the lines in its heading. Pure.
 */

export const NO_SUCH_POSTING = "There is no such posting in the catalogue.";

/** Whether the catalogue answered that it has no posting by that id. */
export function isNoSuchPosting(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404;
}

/** Why the posting cannot be shown: none by that id, or what went wrong. */
export function postingErrorLine(error: unknown): string {
    return isNoSuchPosting(error) ? NO_SUCH_POSTING : describeError(error);
}

/** The tab's name: the title at the company, or Job while it is read. */
export function postingPageName(posting: Pick<Posting, "title" | "company"> | undefined): string {
    return posting ? `${posting.title} at ${posting.company}` : "Job";
}

/** The name of a posting's check page, in the tab and the server's head alike. */
export function checkPageName(posting: Pick<Posting, "title" | "company">): string {
    return `Resume keywords for ${postingPageName(posting)}`;
}

/** The fit shown: at the match once there is an application, else the resume's fit now. */
export function shownFit(
    application: Application | null,
    atMatch: ApplicationFit | undefined,
    fitsNow: Readonly<Record<string, PostingFit>> | null | undefined,
    postingId: string,
): FitView | null {
    if (application) return atMatch ? fitAtMatch(atMatch) : null;
    const now = fitsNow?.[postingId];
    return now ? fitNow(now) : null;
}

/** A model's name as the gateway lists it, or its id when it is not listed. */
export function modelNamer(models: readonly LlmModel[] | undefined): (model: string) => string {
    return (model) => models?.find((entry) => entry.model === model)?.display_name ?? model;
}

/** Which side of the sent card is open: the resume that went, or what the tailor changed. */
export type SentSide = "sent" | "changes";

export function sentSideOf(params: URLSearchParams): SentSide {
    return params.get("view") === "changes" ? "changes" : "sent";
}

/** The address with the side set; the resume that went is the default and is left out. */
export function withSentSide(params: URLSearchParams, side: SentSide): URLSearchParams {
    const next = new URLSearchParams(params);
    if (side === "changes") next.set("view", "changes");
    else next.delete("view");
    return next;
}

/** The last pill: when it closed, or when the catalogue last saw it. */
export function seenLine(
    posting: Pick<Posting, "closed_at" | "last_seen_at">,
    applied: boolean,
    now: Date,
    timeZone?: string,
    locale?: string,
): string {
    if (posting.closed_at) return `Closed ${dayOf(posting.closed_at, timeZone)}`;
    const seen = formatRelative(posting.last_seen_at, now, timeZone, locale);
    return applied ? `Still listed, seen ${seen}` : `Seen ${seen}`;
}

/** The pay and the place, as one line over the posting's text once it went. */
export function payAndPlace(posting: Posting): string {
    return [payLine(posting), posting.location].filter(Boolean).join(". ");
}
